import React, { useState, useEffect, useRef } from 'react';
import '../styles/StockPicker.css';

/**
 * Dropdown with checkboxes showing index constituents sorted by weight descending.
 * Props:
 *   catalogue  - [{symbol, weightPct, implemented}]  from /leading-stocks/catalogue
 *   selected   - string[]  currently selected symbols
 *   onChange   - (string[]) => void
 */
const StockPicker = ({ catalogue = [], selected = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (symbol) => {
    const next = selected.includes(symbol)
      ? selected.filter(s => s !== symbol)
      : [...selected, symbol];
    onChange(next);
  };

  const selectAll  = () => onChange(catalogue.map(c => c.symbol));
  const clearAll   = () => onChange([]);

  const label = selected.length === 0
    ? 'None selected'
    : selected.length === catalogue.length
      ? `All ${catalogue.length} stocks`
      : selected.join(', ');

  return (
    <div className="sp-wrapper" ref={ref}>
      <button
        type="button"
        className={`sp-trigger ${open ? 'sp-open' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="sp-label">{label}</span>
        <span className="sp-count">{selected.length}/{catalogue.length}</span>
        <span className="sp-arrow">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="sp-dropdown">
          <div className="sp-actions">
            <button type="button" className="sp-action-btn" onClick={selectAll}>All</button>
            <button type="button" className="sp-action-btn" onClick={clearAll}>None</button>
          </div>

          <div className="sp-list">
            {catalogue.map(item => {
              const checked = selected.includes(item.symbol);
              return (
                <label
                  key={item.symbol}
                  className={`sp-item ${checked ? 'sp-checked' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(item.symbol)}
                  />
                  <span className="sp-sym">{item.symbol}</span>
                  <span className="sp-wt">{item.weightPct}%</span>
                  {item.implemented && (
                    <span className="sp-impl" title="Currently analysed">●</span>
                  )}
                </label>
              );
            })}
          </div>

          <div className="sp-hint">
            ● = already tracked · select to include in lead analysis
          </div>
        </div>
      )}
    </div>
  );
};

export default StockPicker;
